import { Controller } from 'react-hook-form';
import { DEFAULT_SYNTHETIC_ASSETS } from '../../../data/returnData';
import { Field } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';

/**
 * DIVIDEND-YIELDS editor (PLAN.md M7 Strategy — `assumptions.dividendYields`). Per-asset-class
 * dividend-yield DEFAULTS (decimal): when an account does not set its own `dividendYield`, the
 * simulator allocation-weights these over the account's mix. Edits the optional
 * `assumptions.dividendYields` record; leaving it empty clears it back to undefined (full
 * back-compat). Asset-class ids align with the allocation editor's known ids.
 */

export interface DividendYieldsEditorProps {
  form: SectionProps['form'];
}

const KNOWN_ASSET_IDS = DEFAULT_SYNTHETIC_ASSETS.map((a) => a.id);

export function DividendYieldsEditor({ form }: DividendYieldsEditorProps): React.ReactElement {
  return (
    <Controller
      control={form.control}
      name="assumptions"
      render={({ field }) => {
        const assumptions = field.value as { dividendYields?: Record<string, number> } | undefined;
        const record = assumptions?.dividendYields ?? {};
        const entries = Object.entries(record);

        const commit = (next: Record<string, number>): void => {
          // Empty map → drop `dividendYields` (and `assumptions` if that was its only key).
          if (Object.keys(next).length === 0) {
            field.onChange(undefined);
          } else {
            field.onChange({ ...assumptions, dividendYields: next });
          }
        };
        const setEntry = (key: string, value: number): void => commit({ ...record, [key]: value });
        const renameEntry = (oldKey: string, newKey: string): void => {
          if (newKey === oldKey) return;
          const next: Record<string, number> = {};
          for (const [k, v] of entries) next[k === oldKey ? newKey : k] = v;
          commit(next);
        };
        const removeEntry = (key: string): void => {
          const next = { ...record };
          delete next[key];
          commit(next);
        };
        const addEntry = (): void => {
          const candidate = KNOWN_ASSET_IDS.find((id) => !(id in record)) ?? '';
          commit({ ...record, [candidate]: 0.02 });
        };

        return (
          <Field
            label="Dividend-yield defaults"
            hint="Per asset class (decimal). An account's own yield overrides these."
          >
            <div className="alloc">
              {entries.length === 0 && <p className="alloc__empty">No defaults set.</p>}
              {entries.map(([key, value]) => (
                <div key={key} className="alloc__row">
                  <input
                    className="input alloc__asset"
                    aria-label="Asset class id"
                    list="dividend-asset-ids"
                    value={key}
                    onChange={(e) => renameEntry(key, e.currentTarget.value)}
                  />
                  <input
                    className="input input--num alloc__weight"
                    type="number"
                    step="0.005"
                    min={0}
                    aria-label={`${key} yield`}
                    value={Number.isNaN(value) ? '' : value}
                    onChange={(e) => setEntry(key, e.currentTarget.valueAsNumber)}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost alloc__remove"
                    onClick={() => removeEntry(key)}
                    aria-label={`Remove ${key}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <datalist id="dividend-asset-ids">
                {KNOWN_ASSET_IDS.map((id) => (
                  <option key={id} value={id} />
                ))}
              </datalist>
              <button type="button" className="btn alloc__add" onClick={addEntry}>
                + Add asset class
              </button>
            </div>
          </Field>
        );
      }}
    />
  );
}
